import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Spinner, Badge, Card, Nav, Tab } from 'react-bootstrap';
import { getVehicleById, updateVehicle } from '../services/vehicleService';
import { useAuth } from '../context/AuthContext';
import { uploadDocument, deleteDocument } from '../services/documentService';
import { FaFileAlt, FaUpload, FaEye, FaTrash, FaExclamationTriangle, FaCar, FaMoneyBillWave, FaShieldAlt, FaClipboardList, FaPlus, FaCalculator } from 'react-icons/fa';
import { addExpense, deleteExpense } from '../services/api';
import api from '../services/api';

// ✅ LEAN IMPORTS - Data Transformer & Business Rules
import {
    transformBackendToFrontend,
    transformForApiUpdate,
    invalidateVehicleCache
} from '../transformers/vehicleTransformer';

import {
    validateStatusChange,
    getStatusLabel,
    getStatusVariant,
    getEnhancedStatusBadge,
    analyzeInsuranceStatus,
    validateTrafficInsuranceForm
} from '../utils/vehicleBusinessRules';

import { createVehicleForPurpose } from '../schemas/vehicleSchema';

// ✅ LEAN FORM COMPONENTS
import VehicleBasicInfoForm from '../components/forms/VehicleBasicInfoForm';
import CarExpertizMap from '../components/CarExpertizMap';

import VehiclePriceForm from '../components/forms/VehiclePriceForm';
import TrafficInsuranceForm from '../components/forms/TrafficInsuranceForm';
import VehicleExpenseForm, { expenseTypes } from '../components/forms/VehicleExpenseForm';

// ✅ DOCUMENT COMPONENTS
import DocumentViewer from '../components/DocumentViewer';
import useOCR from '../hooks/useOCR';


const REQUIRED_DOCUMENTS = [
    { category: 'alis_sozlesmesi', name: 'Alış Sözleşmesi' },
    { category: 'satis_sozlesmesi', name: 'Satış Sözleşmesi' },
    { category: 'ruhsat', name: 'Ruhsat' },
    { category: 'alis_faturasi', name: 'Alış Faturası' },
    { category: 'satis_faturasi', name: 'Satış Faturası' },
    { category: 'ekspertiz_raporu', name: 'Ekspertiz Raporu' }
];

const EditVehiclePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // ✅ TAB STATE
    const [activeTab, setActiveTab] = useState('documents');

    // ✅ LEAN STATE MANAGEMENT
    const [vehicle, setVehicle] = useState(() => createVehicleForPurpose('edit'));
    const [originalVehicle, setOriginalVehicle] = useState(null); // For comparison

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // ✅ SEARCH STATE'LERİ - AddVehiclePage'den kopyalandı
    const [searchingSupplier, setSearchingSupplier] = useState(false);
    const [foundSupplier, setFoundSupplier] = useState(null);
    const [supplierSearchError, setSupplierSearchError] = useState('');
    const [identityType, setIdentityType] = useState('TCKN');

    const [searchingCustomer, setSearchingCustomer] = useState(false);
    const [foundCustomer, setFoundCustomer] = useState(null);
    const [customerSearchError, setCustomerSearchError] = useState('');
    const [customerIdentityType, setCustomerIdentityType] = useState('TCKN');
    // ✅ YENİ: OCR STATE'LERİ
    const { processDocument, getExtractedData, getFieldHighlightClass } = useOCR();
    const [ocrFields, setOcrFields] = useState(new Set());

    // ✅ SUPPLIER SEARCH FUNCTION - AddVehiclePage'den kopyalandı
    const handleSearchSupplier = useCallback(async () => {
        if (!vehicle.purchasedFrom?.idNumber || vehicle.purchasedFrom.idNumber.length < 10) {
            setSupplierSearchError('Geçerli bir kimlik numarası giriniz');
            return;
        }

        setSearchingSupplier(true);
        setSupplierSearchError('');
        setError('');
        setSuccessMessage('');

        try {
            const response = await api.get(`/customers/search/${vehicle.purchasedFrom.idNumber}`);

            if (response.data.success && response.data.customers.length > 0) {
                const supplier = response.data.customers[0];
                setFoundSupplier(supplier);

                // Supplier bilgilerini otomatik doldur
                setVehicle(prev => ({
                    ...prev,
                    purchasedFrom: {
                        name: supplier.name || prev.purchasedFrom.name,
                        phone: supplier.phone || prev.purchasedFrom.phone,
                        email: supplier.email || prev.purchasedFrom.email,
                        address: supplier.address || prev.purchasedFrom.address,
                        idNumber: supplier.identifierNumber || prev.purchasedFrom.idNumber
                    }
                }));

                setSuccessMessage(`✅ ${supplier.name} tedarikçisi bulundu ve bilgiler otomatik dolduruldu!`);
            } else {
                setSupplierSearchError('Bu kimlik numarasına ait tedarikçi bulunamadı');
                setFoundSupplier(null);
            }
        } catch (error) {
            console.error('Supplier arama hatası:', error);
            if (error.response?.status === 404) {
                setSupplierSearchError('Bu kimlik numarasına ait tedarikçi bulunamadı');
            } else {
                setSupplierSearchError('Tedarikçi aranırken hata oluştu. Lütfen tekrar deneyin.');
            }
            setFoundSupplier(null);
        } finally {
            setSearchingSupplier(false);
        }
    }, [vehicle.purchasedFrom?.idNumber, identityType]);

    // ✅ CUSTOMER SEARCH FUNCTION - AddVehiclePage'den kopyalandı  
    const handleSearchCustomer = useCallback(async () => {
        if (!vehicle.soldTo?.idNumber || vehicle.soldTo.idNumber.length < 10) {
            setCustomerSearchError('Geçerli bir kimlik numarası giriniz');
            return;
        }

        setSearchingCustomer(true);
        setCustomerSearchError('');
        setError('');
        setSuccessMessage('');

        try {
            const response = await api.get(`/customers/search/${vehicle.soldTo.idNumber}`);

            if (response.data.success && response.data.customers.length > 0) {
                const customer = response.data.customers[0];
                setFoundCustomer(customer);

                // Customer bilgilerini otomatik doldur
                setVehicle(prev => ({
                    ...prev,
                    soldTo: {
                        name: customer.name || prev.soldTo?.name || '',
                        phone: customer.phone || prev.soldTo?.phone || '',
                        email: customer.email || prev.soldTo?.email || '',
                        address: customer.address || prev.soldTo?.address || '',
                        idNumber: customer.identifierNumber || prev.soldTo?.idNumber || ''
                    }
                }));

                setSuccessMessage(`✅ ${customer.name} müşterisi bulundu ve bilgiler otomatik dolduruldu!`);
            } else {
                setCustomerSearchError('Bu kimlik numarasına ait müşteri bulunamadı');
                setFoundCustomer(null);
            }
        } catch (error) {
            console.error('Customer arama hatası:', error);
            if (error.response?.status === 404) {
                setCustomerSearchError('Bu kimlik numarasına ait müşteri bulunamadı');
            } else {
                setCustomerSearchError('Müşteri aranırken hata oluştu. Lütfen tekrar deneyin.');
            }
            setFoundCustomer(null);
        } finally {
            setSearchingCustomer(false);
        }
    }, [vehicle.soldTo?.idNumber, customerIdentityType]);

    // ✅ YENİ: OCR DATA HANDLER
    const handleOCRDataExtracted = useCallback(async (extractedData, mappedFields) => {

        let updatedVehicle = { ...vehicle };
        let appliedFields = 0;

        // Apply OCR data to vehicle
        Object.entries(extractedData).forEach(([docType, data]) => {
            Object.entries(data).forEach(([key, value]) => {
                if (key.includes('.')) {
                    // Nested objects (purchasedFrom.name, soldTo.name)
                    const [parent, child] = key.split('.');
                    if (!updatedVehicle[parent]) updatedVehicle[parent] = {};
                    updatedVehicle[parent][child] = value;
                } else {
                    // Direct fields
                    updatedVehicle[key] = value;
                }
                appliedFields++;
            });
        });

        // Update vehicle data
        setVehicle(updatedVehicle);

        // Update OCR fields for highlighting - ADD to existing
        setOcrFields(prev => {
            const newSet = new Set(prev);
            mappedFields.forEach(field => newSet.add(field));
            return newSet;
        });

        // Success message
        setSuccessMessage(`🎉 OCR tamamlandı! ${appliedFields} alan otomatik güncellendi. Sarı işaretli alanları kontrol edin.`);
        setTimeout(() => setSuccessMessage(''), 5000);
    }, [vehicle]);

    // ✅ DOCUMENT REFRESH STATE
    const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);

    // ✅ EXPENSE MANAGEMENT STATE - LEAN VERSION
    const [showExpenseForm, setShowExpenseForm] = useState(false);

    // ✅ TAB NAVIGATION CONFIG - EXPENSES TAB ADDED
    const tabs = [
        { key: 'documents', title: 'Belgeler', icon: FaClipboardList, color: 'purple' },
        { key: 'basic', title: 'Temel Bilgiler', icon: FaCar, color: 'primary' },
        { key: 'price', title: 'Fiyat', icon: FaMoneyBillWave, color: 'success' },
        { key: 'insurance', title: 'Trafik Sigortası', icon: FaShieldAlt, color: 'info' },       
        
        { key: 'expenses', title: 'Masraflar', icon: FaCalculator, color: 'danger' }
    ];

    // ✅ MEMOIZED CALCULATIONS
    const hasChanges = useMemo(() => {
        if (!originalVehicle) return false;
        return JSON.stringify(vehicle) !== JSON.stringify(originalVehicle);
    }, [vehicle, originalVehicle]);

    const insuranceAnalysis = useMemo(() => {
        return analyzeInsuranceStatus(vehicle);
    }, [vehicle.trafficInsurance, vehicle.status]);

    const formValidation = useMemo(() => {
        return validateTrafficInsuranceForm(vehicle.trafficInsurance, vehicle.status);
    }, [vehicle.trafficInsurance, vehicle.status]);

    // ✅ EXPENSE CALCULATIONS - USING vehicle.expenses (LEAN)
    const getTotalExpenses = useCallback(() => {
        return (vehicle.expenses || []).reduce((total, exp) => total + (exp.amount || 0), 0);
    }, [vehicle.expenses]);

    const getExpenseTypeInfo = useCallback((typeValue) => {
        return expenseTypes.find(type => type.value === typeValue);
    }, []);

    const formatAmount = useCallback((amount) => {
        return new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount);
    }, []);

    // ✅ FETCH VEHICLE DATA - INCLUDES EXPENSES (LEAN)
    const fetchVehicleData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            console.log('🔄 Fetching vehicle data for edit:', id);

            // ✅ FIX: Cache bypass for fresh data
            const response = await getVehicleById(id + '?cache_bust=' + Date.now());

            if (response && response.success && response.vehicle) {
                console.log('✅ Raw backend data:', response.vehicle);

                // ✅ USE TRANSFORMER PATTERN
                const transformedVehicle = transformBackendToFrontend(response.vehicle);
                console.log('🔄 Transformed for frontend:', transformedVehicle);

                setVehicle(transformedVehicle);
                setOriginalVehicle(JSON.parse(JSON.stringify(transformedVehicle)));
            } else {
                setError('Araç verileri yüklenemedi');
            }
        } catch (err) {
            console.error('❌ Vehicle fetch error:', err);
            setError('Araç bilgileri alınırken bir hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    // ✅ LEAN DATA FETCHING - WITH TRANSFORMER
    useEffect(() => {
        fetchVehicleData();
    }, [fetchVehicleData]);

    // ✅ OPTIMIZED EVENT HANDLERS
    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;

        setVehicle(prev => {
            if (type === 'checkbox') {
                return { ...prev, [name]: checked };
            }

            if (name.includes('.')) {
                const [parent, child] = name.split('.');
                return {
                    ...prev,
                    [parent]: {
                        ...prev[parent],
                        [child]: value === '' ? null : value  
                    }
                };
            }
            console.log('DEGISEN ALAN:', name, 'DEGER:', value, 'NULL_YAPILDI_MI:', value === '' ? 'EVET' : 'HAYIR');

            return { ...prev, [name]: value === '' ? '' : value };

        });
    }, []);

    // ✅ EXPENSE HANDLERS - LEAN VERSION
    const handleExpenseAdded = useCallback(() => {
        fetchVehicleData(); // Refresh vehicle data including expenses
        setSuccessMessage('✅ Masraf başarıyla eklendi!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowExpenseForm(false);
    }, [fetchVehicleData]);

    const handleExpenseDelete = useCallback(async (expenseId) => {
        if (!window.confirm('Bu masrafı silmek istediğinizden emin misiniz?')) {
            return;
        }

        try {
            console.log('🗑️ Masraf siliniyor:', { vehicleId: id, expenseId });

            // ✅ DOĞRU ENDPOINT: vehicles/{vehicleId}/expenses/{expenseId}
            const response = await api.delete(`/vehicles/${id}/expenses/${expenseId}`);

            if (response.data.success) {
                fetchVehicleData(); // Refresh vehicle data including expenses
                setSuccessMessage('✅ Masraf başarıyla silindi!');
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (error) {
            console.error('❌ Masraf silme hatası:', error);
            setError('Masraf silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
        }
    }, [fetchVehicleData, id]);

    // ✅ DOCUMENT UPLOAD SUCCESS HANDLER
    const handleDocumentUploadSuccess = useCallback((newDocument) => {
        console.log('✅ Document upload success:', newDocument);
        setSuccessMessage('📄 Belge başarıyla yüklendi!');
        setTimeout(() => setSuccessMessage(''), 3000);

        // Refresh DocumentViewer
        setDocumentsRefreshKey(prev => prev + 1);
    }, []);


    // ✅ LEAN FORM SUBMISSION - WITH TRANSFORMER
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        // ✅ ROOT CAUSE DETECTIVE
        const form = e.target;
        const invalidFields = form.querySelectorAll(':invalid');

        console.log('🔍 FORM VALIDITY:', form.checkValidity());
        console.log('🔍 INVALID FIELDS COUNT:', invalidFields.length);

        invalidFields.forEach((field, index) => {
            console.log(`❌ INVALID FIELD ${index + 1}:`, {
                name: field.name,
                value: field.value,
                validity: field.validity,
                validationMessage: field.validationMessage,
                required: field.required,
                type: field.type
            });
        });

        // ✅ Eğer invalid field varsa durur, devam etmez
        if (!form.checkValidity()) {
            console.log('❌ FORM INVALID - SUBMISSION STOPPED');
            form.reportValidity();
            return;
        }

        setSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            // ✅ BASIC VALIDATION - MOTOR NUMARASI KALDIRILD
            if (!vehicle.plateNumber || !vehicle.chassisNumber) {
                throw new Error('Plaka ve şase numarası zorunludur!');
            }

            if (!vehicle.purchasePrice) {
                throw new Error('Alış fiyatı zorunludur!');
            }

            // ✅ INSURANCE VALIDATION
            if (!formValidation.isValid) {
                throw new Error('Trafik sigortası bilgileri eksik: ' + formValidation.errors.join(', '));
            }

            // ✅ USE TRANSFORMER FOR BACKEND
            const backendData = transformForApiUpdate(vehicle);
            backendData.kaskoKodu = vehicle.kaskoKodu;
            backendData.kaskoBedeli = vehicle.kaskoBedeli;
     
            const response = await updateVehicle(id, backendData);

            if (response && response.success) {
                // ✅ CRITICAL: Clear cache for this vehicle
                invalidateVehicleCache(id);

                setSuccessMessage('✅ Araç başarıyla güncellendi!');

                // Update original vehicle for change detection
                setOriginalVehicle(JSON.parse(JSON.stringify(vehicle)));

                // Navigate after delay
                setTimeout(() => {
                    navigate(`/vehicles/${id}`);
                }, 1500);
            } else {
                throw new Error(response?.message || 'Araç güncellenirken bir hata oluştu');
            }
        } catch (err) {
            console.error('❌ Submit error:', err);
            setError(err.message || 'Araç kaydedilirken bir hata oluştu');
        } finally {
            setSaving(false);
        }
    }, [vehicle, formValidation, id, navigate]);

    // ✅ LOADING STATE
    if (loading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
                <p className="mt-3">Araç verileri yükleniyor...</p>
            </Container>
        );
    }

    // ✅ LEAN RENDER
    return (
        <Container className="py-5">
            {/* ✅ ENHANCED HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="mb-2">🔧 Araç Bilgilerini Düzenle</h1>
                    <p className="text-muted mb-0">
                        {vehicle.brand} {vehicle.model} {vehicle.year} - {vehicle.plateNumber}
                    </p>
                </div>
                <Badge bg={getStatusVariant(vehicle.status)}>
                    {getStatusLabel(vehicle.status)}
                </Badge>
            </div>

            {/* ✅ LEAN ALERTS */}
            {error && <Alert variant="danger">{error}</Alert>}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            {/* ✅ CHANGES INDICATOR */}
            {hasChanges && (
                <Alert variant="warning" className="mb-4">
                    <FaExclamationTriangle className="me-2" />
                    <strong>Kaydedilmemiş değişiklikler var!</strong> Değişiklikleri kaydetmeyi unutmayın.
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col lg={8}>
                        {/* ✅ TAB NAVIGATION */}
                        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                            <Card className="mb-4">
                                <Card.Header className="p-0">
                                    <Nav variant="tabs" className="border-0">
                                        {tabs.map(tab => {
                                            const IconComponent = tab.icon;
                                            return (
                                                <Nav.Item key={tab.key}>
                                                    <Nav.Link
                                                        eventKey={tab.key}
                                                        className={`d-flex align-items-center px-3 py-3 ${activeTab === tab.key ? 'active' : ''}`}
                                                    >
                                                        <IconComponent
                                                            className={`me-2 text-${tab.color}`}
                                                            size={16}
                                                        />
                                                        <span className="fw-bold">{tab.title}</span>
                                                    </Nav.Link>
                                                </Nav.Item>
                                            );
                                        })}
                                    </Nav>
                                </Card.Header>

                                <Card.Body className="p-4">
                                    <Tab.Content>
                                        <Tab.Pane eventKey="basic">
                                            <VehicleBasicInfoForm
                                                data={vehicle}
                                                handleChange={handleChange}
                                                disabled={saving}
                                                validationErrors={{}}
                                                getFieldHighlightClass={getFieldHighlightClass}
                                            />
                                        </Tab.Pane>

                                        <Tab.Pane eventKey="price">
                                            <VehiclePriceForm
                                                data={vehicle}
                                                handleChange={handleChange}
                                                disabled={saving}
                                                getFieldHighlightClass={getFieldHighlightClass}
                                            // ✅ SEARCH PROPS
                                                identityType={identityType}
                                                setIdentityType={setIdentityType}
                                                searchingSupplier={searchingSupplier}
                                                foundSupplier={foundSupplier}
                                                supplierSearchError={supplierSearchError}
                                                handleSearchSupplier={handleSearchSupplier}
                                                customerIdentityType={customerIdentityType}
                                                setCustomerIdentityType={setCustomerIdentityType}
                                                searchingCustomer={searchingCustomer}
                                                foundCustomer={foundCustomer}
                                                customerSearchError={customerSearchError}
                                                handleSearchCustomer={handleSearchCustomer}
                                                setFormData={setVehicle}
                                            />
                                        </Tab.Pane>

                                        <Tab.Pane eventKey="insurance">
                                            <TrafficInsuranceForm
                                                data={vehicle}
                                                handleChange={handleChange}
                                                mode="edit"
                                                vehicleStatus={vehicle.status}
                                                disabled={saving}
                                            />
                                        </Tab.Pane>

                                        {/* ✅ NEW EXPENSES TAB */}
                                        <Tab.Pane eventKey="expenses">
                                            <Card>
                                                <Card.Header>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <h5 className="mb-0">💸 Masraf Yönetimi</h5>
                                                            <small className="text-muted">
                                                                Araç ile ilgili masrafları yönetin
                                                            </small>
                                                        </div>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={() => {                                                          
                                                                setShowExpenseForm(true);
                                                            }}                                                        >
                                                            <FaPlus className="me-1" />
                                                            Masraf Ekle
                                                        </Button>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body>
                                                    {/* Masraf özeti */}
                                                    {vehicle.expenses && (
                                                        <Row className="mb-3">
                                                            <Col md={4} className="text-center">
                                                                <div className="bg-info text-white rounded p-2">
                                                                    <strong>{vehicle.expenses.length}</strong>
                                                                    <small className="d-block">Toplam Masraf</small>
                                                                </div>
                                                            </Col>
                                                            <Col md={4} className="text-center">
                                                                <div className="bg-danger text-white rounded p-2">
                                                                    <strong>{formatAmount(getTotalExpenses())} ₺</strong>
                                                                    <small className="d-block">Toplam Tutar</small>
                                                                </div>
                                                            </Col>
                                                            <Col md={4} className="text-center">
                                                                <div className="bg-success text-white rounded p-2">
                                                                    <strong>
                                                                        {vehicle.expenses.length > 0 ? formatAmount(getTotalExpenses() / vehicle.expenses.length) : '0'} ₺
                                                                    </strong>
                                                                    <small className="d-block">Ortalama</small>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    )}

                                                    {/* Masraflar listesi */}
                                                    {vehicle.expenses && vehicle.expenses.length > 0 ? (
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-hover">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Tür</th>
                                                                        <th>Açıklama</th>
                                                                        <th>Tutar</th>
                                                                        <th>Tarih</th>
                                                                        <th>Satıcı</th>
                                                                        <th>Ekleyen</th>

                                                                        <th>İşlem</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {vehicle.expenses.map(expense => {
                                                                        const typeInfo = getExpenseTypeInfo(expense.type);
                                                                        const IconComponent = typeInfo?.icon || FaCalculator;

                                                                        return (
                                                                            <tr key={expense._id || expense.id}>
                                                                                <td>
                                                                                    <div className="d-flex align-items-center">
                                                                                        <IconComponent
                                                                                            className={`text-${typeInfo?.color || 'secondary'} me-2`}
                                                                                            size={16}
                                                                                        />
                                                                                        <div>
                                                                                            <div style={{ fontSize: '0.9em', fontWeight: 'bold' }}>
                                                                                                {typeInfo?.label || expense.type}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div style={{ fontSize: '0.9em' }}>
                                                                                        {expense.description}
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <Badge bg="success">
                                                                                        {formatAmount(expense.amount)} ₺
                                                                                    </Badge>
                                                                                </td>
                                                                                <td style={{ fontSize: '0.8em' }}>
                                                                                    {new Date(expense.date).toLocaleDateString('tr-TR')}
                                                                                </td>
                                                                                <td style={{ fontSize: '0.8em' }}>
                                                                                    {expense.vendor || '-'}
                                                                                </td>
                                                                                <td style={{ fontSize: '0.8em' }}>
                                                                                    {expense.addedBy?.name || 'Bilinmiyor'}
                                                                                </td>
                                                                                <td>
                                                                                    <Button
                                                                                        variant="outline-danger"
                                                                                        size="sm"
                                                                                        onClick={() => handleExpenseDelete(expense._id || expense.id)}
                                                                                    >
                                                                                        <FaTrash size={12} />
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4 text-muted">
                                                                <FaCalculator size={40} className="mb-3 text-muted" />
                                                            <p>Henüz masraf eklenmedi</p>
                                                            <Button
                                                                variant="outline-primary"
                                                                onClick={() => setShowExpenseForm(true)}
                                                            >
                                                                <FaPlus className="me-1" />
                                                                İlk masrafı ekle
                                                            </Button>
                                                        </div>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Tab.Pane>

                                        <Tab.Pane eventKey="documents">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h5 className="mb-0">
                                                        <FaFileAlt className="me-2" />
                                                        Belgeler
                                                    </h5>
                                                </div>
                                                <div className="card-body">
                                                    <DocumentViewer
                                                        key={documentsRefreshKey}
                                                        vehicleId={id}
                                                        showActions={true}
                                                        isAdmin={currentUser?.userType === 'admin'}
                                                        currentUserId={currentUser?._id}
                                                        onDocumentUpdate={() => {
                                                            setDocumentsRefreshKey(prev => prev + 1);
                                                        }}
                                                        onUploadSuccess={handleDocumentUploadSuccess}
                                                        onOCRDataExtracted={handleOCRDataExtracted}
                                                    />
                                                </div>
                                            </div>
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Card.Body>
                            </Card>
                        </Tab.Container>
                    </Col>

                    <Col lg={4}>
                        {/* ✅ LEAN SIDEBAR */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0">📋 Araç Özeti</h5>
                            </div>
                            <div className="card-body">
                                <p><strong>Araç:</strong> {vehicle.brand} {vehicle.model}</p>
                                <p><strong>Yıl:</strong> {vehicle.year}</p>
                                <p><strong>Plaka:</strong> {vehicle.plateNumber}</p>
                                <p><strong>Alış Fiyatı:</strong> {vehicle.purchasePrice ? `${Number(vehicle.purchasePrice).toLocaleString()} ₺` : 'Belirtilmedi'}</p>
                                <p><strong>Satış Fiyatı:</strong> {vehicle.salePrice ? `${Number(vehicle.salePrice).toLocaleString()} ₺` : 'Belirtilmedi'}</p>
                                <p><strong>Durum:</strong>
                                    <Badge bg={getStatusVariant(vehicle.status)} className="ms-2">
                                        {getStatusLabel(vehicle.status)}
                                    </Badge>
                                </p>
                                <p><strong>Tramer:</strong>
                                    <span className={`badge ms-2 ${vehicle.hasTramer ? 'bg-danger' : 'bg-success'}`}>
                                        {vehicle.hasTramer ? `Var (${vehicle.tramerAmount} ₺)` : 'Yok'}
                                    </span>
                                </p>
                                {vehicle.expenses && (
                                    <p><strong>Masraflar:</strong>
                                        <Badge bg="warning" className="ms-2">
                                            {vehicle.expenses.length} adet - {formatAmount(getTotalExpenses())} ₺
                                        </Badge>
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* ✅ STATUS DISPLAY CARD (READ-ONLY) */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h6 className="mb-0">📊 Araç Durumu</h6>
                            </div>
                            <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className="text-muted">Mevcut Durum:</span>
                                    <Badge bg={getStatusVariant(vehicle.status)} className="fs-6 px-3 py-2">
                                        {getStatusLabel(vehicle.status)}
                                    </Badge>
                                </div>
                                <div className="mt-2">
                                    <small className="text-muted">
                                        ℹ️ Araç durumu otomatik olarak güncellenir (satış, rezervasyon vb.)
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* ✅ ACTION BUTTONS */}
                        <div className="d-grid gap-2">
                            <Button
                                variant="primary"
                                type="submit"
                                size="lg"
                                disabled={saving || !hasChanges}
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Kaydediliyor...
                                    </>
                                ) : hasChanges ? (
                                    '💾 Değişiklikleri Kaydet'
                                ) : (
                                    '✅ Değişiklik Yok'
                                )}
                            </Button>

                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(`/vehicles/${id}`)}
                                size="lg"
                                disabled={saving}
                            >
                                ❌ İptal
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>

            {/* ✅ EXPENSE FORM MODAL */}
            <VehicleExpenseForm
                vehicleId={id}  // Normal ID (not temp)
                isOpen={showExpenseForm}
                onClose={() => {
                    console.log('🔥 VehicleExpenseForm onClose called');
                    setShowExpenseForm(false);
                }}
                onExpenseAdded={handleExpenseAdded}
            />
        </Container>
    );
};

export default EditVehiclePage;